import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import {
  acceptStaffInvitation,
  previewStaffInvitation,
} from '../api/staffInvitations.js'
import { useAuth } from '../auth/AuthProvider.jsx'
import { firstError, formatDateTime } from '../utils/format.js'

/**
 * Public staff invitation acceptance page. Path: /accept-invite/staff/:token
 *
 * Mirrors `routes/AcceptInvite.jsx` (which handles doctor invitations)
 * but consumes the polymorphic StaffInvitation flow at
 * /api/staff-invitations/accept/<token>/ — covers pharmacy_staff,
 * lab_staff, radiology_staff, ambulance_staff and org_admin.
 *
 * Flow:
 *   1. GET preview — show who the invite is for + role + target org name.
 *   2. Recipient confirms name (pre-filled if the admin supplied it),
 *      sets a password, submits.
 *   3. POST accept — backend creates User + role-specific Link row +
 *      adds the user to the right Django auth group, returns a JWT pair.
 *   4. We hydrate AuthProvider with the tokens then redirect to "/".
 *      RoleHome routes from there to the role's home (/lab, /pharmacy,
 *      /radiology, /ambulance, or /org/dashboard).
 */
export default function AcceptStaffInvite() {
  const { token } = useParams()
  const { signInWithTokens } = useAuth()
  const nav = useNavigate()

  const preview = useQuery({
    queryKey: ['staff-invitation-preview', token],
    queryFn: () => previewStaffInvitation(token),
    retry: false,
  })
  const inv = preview.data

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  // Pre-fill the name fields once the preview has loaded — admins
  // typically know the recipient's name when they issue the invite, so
  // 90% of the time these come pre-populated and the recipient just
  // confirms by leaving them as-is.
  if (inv && firstName === '' && lastName === '' && (inv.first_name || inv.last_name)) {
    setFirstName(inv.first_name || '')
    setLastName(inv.last_name || '')
  }

  async function onSubmit(e) {
    e.preventDefault()
    setErr(null)
    if (password.length < 8) {
      setErr('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setErr('Passwords do not match.')
      return
    }
    setBusy(true)
    try {
      const resp = await acceptStaffInvitation(token, {
        password,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
      })
      await signInWithTokens({ access: resp.access, refresh: resp.refresh })
      // RoleHome routes to /lab, /pharmacy, /radiology, /ambulance, or
      // /org/dashboard based on the freshly issued JWT's role claim.
      nav('/', { replace: true })
    } catch (e) {
      setErr(firstError(e, 'Could not accept the invitation.'))
    } finally {
      setBusy(false)
    }
  }

  // ---- states -------------------------------------------------------------

  if (preview.isLoading) {
    return <Shell><p className="text-sm text-slate-500">Loading invitation…</p></Shell>
  }

  if (preview.isError || !inv) {
    return (
      <Shell>
        <h2 className="text-lg font-semibold text-slate-800">Invitation link is not valid</h2>
        <p className="text-sm text-slate-500 mt-1">
          This link is unknown, has been revoked, or has already been used. Please
          ask your administrator to issue a fresh invitation.
        </p>
        <Link to="/login" className="inline-block mt-4 text-wantok-700 underline text-sm">
          Return to sign-in
        </Link>
      </Shell>
    )
  }

  if (inv.effective_status !== 'pending') {
    return (
      <Shell>
        <h2 className="text-lg font-semibold text-slate-800">
          This invitation has {inv.effective_status === 'expired' ? 'expired' : `been ${inv.effective_status}`}
        </h2>
        <p className="text-sm text-slate-500 mt-2">
          Invited: <b>{[inv.first_name, inv.last_name].filter(Boolean).join(' ') || inv.email}</b>
          <br />Email: {inv.email}
        </p>
        <p className="text-sm text-slate-500 mt-3">
          Ask your administrator to issue a fresh invitation.
        </p>
        <Link to="/login" className="inline-block mt-4 text-wantok-700 underline text-sm">
          Return to sign-in
        </Link>
      </Shell>
    )
  }

  // ---- happy path ---------------------------------------------------------

  // Friendly greeting copy depends on the role. Org admins get "Welcome,"
  // (they're not joining as staff per se); the four provider staff
  // roles get "Welcome, [name]" with role + provider name folded in.
  const displayName = [inv.first_name, inv.last_name].filter(Boolean).join(' ') || 'there'
  const isOrgAdmin = inv.target_role === 'org_admin'

  return (
    <Shell>
      <h2 className="text-lg font-semibold text-slate-800">
        Welcome, {displayName}
      </h2>
      <p className="text-sm text-slate-500 mt-1">
        {isOrgAdmin
          ? <>You've been invited to manage <b>{inv.target_name}</b> on Wantok Telehealth.</>
          : <>You've been invited to join <b>{inv.target_name}</b> on Wantok Telehealth as <b>{inv.role_label}</b>.</>}
      </p>

      <div className="mt-3 text-xs text-slate-500 space-y-0.5 bg-slate-50 border border-slate-200 rounded p-2">
        <div>Email: <span className="font-medium">{inv.email}</span></div>
        {inv.is_admin && (
          <div className="text-amber-800">
            <b>Admin access</b> — {isOrgAdmin
              ? 'primary contact for this organisation'
              : `manage your ${inv.role_label?.toLowerCase().replace(' staff', '')} catalogue and configuration`}
          </div>
        )}
        {inv.invited_by_name && <div>Issued by: {inv.invited_by_name}</div>}
        {inv.expires_at && <div>Invitation valid until: {formatDateTime(inv.expires_at)}</div>}
      </div>

      <form onSubmit={onSubmit} className="mt-5 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-slate-600 flex flex-col gap-1">
            <span>First name</span>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              className="border rounded px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-xs text-slate-600 flex flex-col gap-1">
            <span>Last name</span>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              className="border rounded px-2 py-1.5 text-sm"
            />
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-slate-600 flex flex-col gap-1">
            <span>Password (min 8) *</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="border rounded px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-xs text-slate-600 flex flex-col gap-1">
            <span>Confirm password *</span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="border rounded px-2 py-1.5 text-sm"
            />
          </label>
        </div>

        {err && (
          <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2 text-sm">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full bg-wantok-700 hover:bg-wantok-800 text-white rounded py-2 text-sm disabled:opacity-60"
        >
          {busy ? 'Creating account…' : 'Accept invitation & sign in'}
        </button>
      </form>

      <p className="text-[11px] text-slate-400 text-center mt-3">
        By accepting you confirm that you are the named recipient and that the
        details above are correct.
      </p>
    </Shell>
  )
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h1 className="text-xl font-semibold text-wantok-800">Wantok Telehealth</h1>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  )
}
